<script setup lang="ts">
  defineOptions({ name: 'CPModal', inheritAttrs: false })

  // External library imports

  import { HLModal } from '@platform-ui/highrise'

  // Type imports

  // Props definitions
  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
    show: {
      type: Boolean,
      required: false,
      default: false,
    },
    width: {
      type: Number,
      required: false,
      default: undefined,
    },
    height: {
      type: Number,
      required: false,
      default: undefined,
    },
    closeOnEsc: {
      type: Boolean,
      required: false,
      default: true,
    },
    maskClosable: {
      type: Boolean,
      required: false,
      default: true,
    },
    zIndex: {
      type: Number,
      required: false,
      default: undefined,
    },
    showHeaderIcon: {
      type: Boolean,
      default: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    'update:show': [show: boolean]
    onEsc: []
    onClose: []
    onOpen: []
  }>()

  // User-defined functions
  const handleUpdateShow = (...args: unknown[]) => {
    emit('update:show', Boolean(args[0]))
  }

  const handleEsc = () => {
    emit('onEsc')
  }

  const handleClose = () => {
    emit('onClose')
  }

  const handleOpen = () => {
    emit('onOpen')
  }
</script>

<template>
  <HLModal
    v-bind="$attrs"
    :id="props.id"
    class="cp-modal"
    :show="props.show"
    :width="props.width"
    :height="props.height"
    :close-on-esc="props.closeOnEsc"
    :show-header-icon="props.showHeaderIcon"
    :mask-closable="props.maskClosable"
    :z-index="props.zIndex"
    @update:show="handleUpdateShow"
    @esc="handleEsc"
    @close="handleClose"
    @after-enter="handleOpen"
  >
    <template v-if="$slots.header" #header>
      <slot name="header" />
    </template>
    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
    <slot />
  </HLModal>
</template>

<!--
  HighRise dialog overrides — unscoped because HLModal teleports its
  surface (and the destructive button restoration block targets buttons
  contextually nested under .hr-dialog) into document.body. <style scoped>
  and :deep() cannot reach teleported nodes.

  Important detail: setting `--n-color: var(--cp-background-500)` with
  !important on .hr-dialog cascades into ALL descendants. That overwrites
  every nested button's own inline --n-color, making primary/destructive
  buttons render background-on-background. The nested .hr-dialog .hr-button
  rules below restore each semantic colour explicitly so primary and error
  buttons remain visible inside dialogs.

  HighRise injects --n-* custom properties as inline styles via its JS
  theme engine, so !important is required to beat inline specificity.
-->
<style>
  /* Scope teleported HighRise dialog tokens to CPModal only. */
  :is(.hr-dialog.cp-modal, .cp-modal .hr-dialog) {
    --n-color: var(--cp-background-500) !important;
    --n-text-color: var(--cp-brand-font) !important;
    --n-title-text-color: var(--cp-font-700) !important;
    --n-close-color: var(--cp-font-600) !important;
    --n-close-color-hover: var(--cp-font-700) !important;
    --n-close-icon-color: var(--cp-font-600) !important;
    --n-close-icon-color-hover: var(--cp-font-700) !important;
  }

  :is(.hr-dialog.cp-modal, .cp-modal .hr-dialog) .hr-dialog__title,
  :is(.hr-dialog.cp-modal, .cp-modal .hr-dialog) .n-dialog__title {
    color: var(--cp-font-700) !important;
  }

  :is(.hr-dialog.cp-modal, .cp-modal .hr-dialog) .hr-dialog__close,
  :is(.hr-dialog.cp-modal, .cp-modal .hr-dialog) .n-dialog__close {
    color: var(--cp-font-600) !important;
  }

  /*
   * Re-establish button fill colours inside modals.
   *
   * CSS custom properties with !important cascade into ALL descendants.
   * .hr-dialog sets --n-color to the background token which overwrites the
   * button's own inline --n-color, making primary buttons invisible (they render
   * background-colour-on-background-colour at 45% opacity in disabled state).
   * Restore each semantic colour explicitly here.
   */
  :is(.hr-dialog.cp-modal, .cp-modal .hr-dialog) .hr-button.hr-button--primary {
    --n-color: var(--cp-primary-500) !important;
    --n-color-hover: var(--cp-primary-600) !important;
    --n-color-pressed: var(--cp-primary-700) !important;
    --n-color-focus: var(--cp-primary-500) !important;
    --n-text-color: var(--cp-text-on-primary) !important;
    --n-text-color-hover: var(--cp-text-on-primary) !important;
    --n-text-color-pressed: var(--cp-text-on-primary) !important;
    --n-text-color-focus: var(--cp-text-on-primary) !important;
    --n-border: 1px solid var(--cp-primary-500) !important;
    --n-border-hover: 1px solid var(--cp-primary-600) !important;
    --n-border-pressed: 1px solid var(--cp-primary-700) !important;
    --n-border-focus: 1px solid var(--cp-primary-500) !important;
    --n-box-shadow-focus: 0 0 0 3px var(--cp-primary-100) !important;
  }

  :is(.hr-dialog.cp-modal, .cp-modal .hr-dialog) .hr-button.hr-button--primary .hr-button__state-border,
  :is(.hr-dialog.cp-modal, .cp-modal .hr-dialog) .hr-button.hr-button--primary .n-button__state-border {
    border-color: transparent !important;
    box-shadow: none !important;
  }

  /* Error-coloured primary button inside dialog */
  :is(.hr-dialog.cp-modal, .cp-modal .hr-dialog) .hr-button.hr-button--primary.hr-button--color-error {
    --n-color: var(--cp-error-500) !important;
    --n-color-hover: var(--cp-error-600) !important;
    --n-color-pressed: var(--cp-error-700) !important;
    --n-text-color: var(--cp-text-on-error) !important;
    --n-border: 1px solid var(--cp-error-500) !important;
  }
</style>
